import { useRef, useState } from 'react';
import { Attachment, MediaImage, Plus } from 'iconoir-react';

import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { useModelStore } from '../../../state/store/chat/use-model-store';
import { modelSupportedExtensionsMap, ModelTypes } from '../types/model.types';
import { useTranslation } from 'react-i18next';
import { ButtonIds } from 'features/chat/types/chat';
import { ChatPlusMenuExtras } from './chat-plus-menu-extras';
import type { MentionPickerItem } from '../utils/normalize-mention-search';

import { useToast } from 'hooks/use-toast';

const fileMenuItemHoverClass = 'rounded-md hover:bg-[#F6F6F6] dark:hover:bg-muted';
const fileMenuItemTextClass = 'text-[#3D3D3D] dark:text-[#ffffff]';
const fileMenuItemLabelClass = 'text-sm font-normal leading-[1.2] text-[#3D3D3D] dark:text-[#ffffff]';

import { MAX_FILE_SIZE } from 'constant/file';
export { MAX_FILE_SIZE };

export interface FileUploadPopoverProps {
  onFilesSelected?: (files: File[]) => void;
  isFeatureSelected?: Record<ButtonIds, boolean>;
  disabled?: boolean;
  onSelectSkill?: (skill: MentionPickerItem) => void;
  onOpenNewProject?: () => void;
  onToggleConnectors?: () => void;
  isConnectorsActive?: boolean;
  onMenuClose?: () => void;
  showExtrasOnMobile?: boolean;
  /** When false, only Add photos / Add files are shown (no skills, projects, connectors). */
  showMenuExtras?: boolean;
  /** Use when the global model store is not the active chat model (e.g. agent chat). */
  modelType?: ModelTypes;
  /** Document extensions hidden from the file picker (e.g. agent chat blocks csv/xlsx/json). */
  excludedDocumentExtensions?: string[];
  /** Always show the + popover trigger (mobile agent chat has no separate mobile menu). */
  showTriggerOnAllBreakpoints?: boolean;
  /** Maximum allowed file size in bytes (defaults to 30MB). */
  maxFileSize?: number;
}

export function FileUploadPopover({
  onFilesSelected,
  isFeatureSelected,
  disabled = false,
  onSelectSkill,
  onOpenNewProject,
  onToggleConnectors,
  isConnectorsActive = false,
  onMenuClose,
  showExtrasOnMobile = true,
  showMenuExtras = true,
  modelType,
  excludedDocumentExtensions,
  showTriggerOnAllBreakpoints = false,
  maxFileSize = MAX_FILE_SIZE,
}: FileUploadPopoverProps = {}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedModel = useModelStore((state) => state.selectedModel);
  const resolvedModelType = modelType ?? selectedModel?.modelType ?? ModelTypes.VISION;
  const [clickOnFiles, setClickOnFiles] = useState(false);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      const validFiles: File[] = [];
      const oversizedFiles: File[] = [];

      selectedFiles.forEach((file) => {
        if (file.size > maxFileSize) {
          oversizedFiles.push(file);
        } else {
          validFiles.push(file);
        }
      });

      if (oversizedFiles.length > 0) {
        toast({
          variant: 'destructive',
          title: t('FILE_SIZE_LIMIT_EXCEEDED') || 'File size limit exceeded',
          description:
            oversizedFiles.length === 1
              ? `${oversizedFiles[0].name} exceeds the 30MB limit.`
              : `${oversizedFiles.length} files exceed the 30MB limit.`,
        });
      }

      if (validFiles.length > 0) {
        onFilesSelected && onFilesSelected(validFiles);
      }
      setOpen(false); // Close the popover after selecting files
      e.target.value = '';
    }
  };

  const handleAddPhotosAndFilesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    fileInputRef.current?.click();
  };

  const handleAddPhotosClick = (e: React.MouseEvent) => {
    setTimeout(() => handleAddPhotosAndFilesClick(e), 100);
    setClickOnFiles(false);
  };

  const handleAddFilesClick = (e: React.MouseEvent) => {
    setTimeout(() => handleAddPhotosAndFilesClick(e), 100);
    setClickOnFiles(true);
  };

  function getDocumentExtensions() {
    const documentExtensions = modelSupportedExtensionsMap[resolvedModelType]?.document ?? [];
    if (!excludedDocumentExtensions?.length) {
      return documentExtensions;
    }
    return documentExtensions.filter((ext) => !excludedDocumentExtensions.includes(ext));
  }

  function getAcceptData() {
    return clickOnFiles
      ? getDocumentExtensions().join(',')
      : (modelSupportedExtensionsMap[resolvedModelType]?.image ?? []).join(',');
  }

  const showPhotosOption = resolvedModelType !== ModelTypes.NO_VISION;

  return (
    <div className="rounded-xl">
      <div
        className={cn(
          'items-center gap-2',
          showTriggerOnAllBreakpoints ? 'flex' : 'hidden xl:flex'
        )}
      >
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              disabled={disabled || (isFeatureSelected ? isFeatureSelected?.[ButtonIds.CREATE_IMAGE] : false)}
              size="icon"
              variant="ghost"
              className={`h-8 min-w-8 hover:rounded-full hover:bg-muted  [&_svg]:!size-7`}
            >
              <Plus
                width={32}
                height={32}
                strokeWidth={1}
                className="!size-7 shrink-0 dark:text-[#ffffff] text-[#3D3D3D]"
              />
              <span className="sr-only">{t('ADD_FILES')}</span>
            </Button>
          </PopoverTrigger>


          <PopoverContent
            className="w-52 overflow-visible"
            align="start"
            side="top"
            sideOffset={10}
            collisionPadding={16}
            onPointerDownOutside={(event) => {
              const target = event.target as HTMLElement | null;
              if (target?.closest('[data-plus-menu-flyout]')) {
                event.preventDefault();
              }
            }}
          >
            <div className="flex flex-col overflow-visible">
              {showPhotosOption && (
                <button
                  className={`flex w-full px-1 items-center gap-3 py-1.5 transition-colors text-left disabled:opacity-50 ${fileMenuItemHoverClass}`}
                  onClick={handleAddPhotosClick}
                >
                  <div className={`flex h-4 w-4 shrink-0 items-center justify-center ${fileMenuItemTextClass}`}>
                    <MediaImage className="h-4 w-4" strokeWidth={1.5} />
                  </div>
                  <span className={fileMenuItemLabelClass}>{t('ADD_PHOTOS')}</span>
                </button>
              )}

              <button
                className={`flex w-full px-1 items-center gap-3 py-1.5 transition-colors text-left disabled:opacity-50 ${fileMenuItemHoverClass}`}
                onClick={handleAddFilesClick}
              >
                <div className={`flex h-4 w-4 shrink-0 items-center justify-center ${fileMenuItemTextClass}`}>
                  <Attachment className="h-4 w-4" strokeWidth={1.5} />
                </div>
                <span className={fileMenuItemLabelClass}>{t('ADD_FILES')}</span>
              </button>

              <input
                type="file"
                accept={getAcceptData()}
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                multiple
              />

              {showMenuExtras && (
                <>
                  <div className="my-2 h-px bg-border" />

                  <ChatPlusMenuExtras
                    onClose={() => {
                      setOpen(false);
                      onMenuClose?.();
                    }}
                    onSelectSkill={onSelectSkill}
                    onOpenNewProject={onOpenNewProject}
                    onToggleConnectors={onToggleConnectors}
                    isConnectorsActive={isConnectorsActive}
                  />
                </>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {!showTriggerOnAllBreakpoints && (
      <div className="xl:hidden flex flex-col">
        {showPhotosOption && (
          <button
            className={`flex w-full px-1 items-center gap-3 py-1.5 transition-colors text-left ${fileMenuItemHoverClass}`}
            onClick={handleAddPhotosClick}
          >
            <div className={`flex h-4 w-4 shrink-0 items-center justify-center ${fileMenuItemTextClass}`}>
              <MediaImage className="h-4 w-4" strokeWidth={1.5} />
            </div>
            <span className={fileMenuItemLabelClass}>{t('ADD_PHOTOS')}</span>
          </button>
        )}

        <button
          className={`flex w-full px-1 items-center gap-3 py-1.5 transition-colors text-left disabled:opacity-50 ${fileMenuItemHoverClass}`}
          onClick={handleAddFilesClick}
        >
          <div className={`flex h-4 w-4 shrink-0 items-center justify-center ${fileMenuItemTextClass}`}>
            <Attachment className="h-4 w-4" strokeWidth={1.5} />
          </div>
          <span className={fileMenuItemLabelClass}>{t('ADD_FILES')}</span>
        </button>

        <input
          type="file"
          accept={getAcceptData()}
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          multiple
        />

        {showExtrasOnMobile && showMenuExtras && (
          <>
            <div className="my-2 h-px bg-border" />

            <ChatPlusMenuExtras
              onClose={onMenuClose}
              onSelectSkill={onSelectSkill}
              onOpenNewProject={onOpenNewProject}
              onToggleConnectors={onToggleConnectors}
              isConnectorsActive={isConnectorsActive}
            />
          </>
        )}
      </div>
      )}
    </div >
  );
}
